import moment from 'moment-timezone';
import { DateRangePresetKey } from '../types';

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const fromLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Serializa data de calendário em YYYY-MM-DD no fuso local (nunca via UTC). */
export const toDateOnlyString = (value?: Date | string | null): string => {
  if (!value) return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return fromLocalDate(value);
  }

  const asString = String(value).trim();
  const iso = asString.match(DATE_ONLY_RE);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const br = moment(asString, 'DD/MM/YYYY', true);
  if (br.isValid()) return br.format('YYYY-MM-DD');

  const parsed = new Date(asString);
  if (!Number.isNaN(parsed.getTime())) {
    return fromLocalDate(parsed);
  }

  return '';
};

/** Interpreta YYYY-MM-DD / Date como meia-noite local, sem deslocar o dia. */
export const parseDateOnly = (value?: Date | string | null): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const dateOnly = toDateOnlyString(value);
  if (!dateOnly) return null;

  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const normalizeDateRange = (
  start: Date | null,
  end: Date | null
): { start: Date | null; end: Date | null } => {
  if (!start && !end) return { start: null, end: null };
  if (start && !end) return { start, end: start };
  if (!start && end) return { start: end, end };
  return { start, end };
};

export type DateRangeValue = {
  start: Date | null;
  end: Date | null;
};

export type DateRangePreset = {
  key: DateRangePresetKey;
  label: string;
  getRange: () => DateRangeValue;
};

const startOfDay = (m: moment.Moment) => m.clone().startOf('day').toDate();
const endOfDay = (m: moment.Moment) => m.clone().endOf('day').toDate();

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    key: 'today',
    label: 'Hoje',
    getRange: () => {
      const today = moment();
      return { start: startOfDay(today), end: endOfDay(today) };
    }
  },
  {
    key: 'yesterday',
    label: 'Ontem',
    getRange: () => {
      const yesterday = moment().subtract(1, 'day');
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    }
  },
  {
    key: 'thisWeek',
    label: 'Esta semana (Dom - Hoje)',
    getRange: () => {
      const today = moment();
      const sunday = today.clone().startOf('week');
      return { start: startOfDay(sunday), end: endOfDay(today) };
    }
  },
  {
    key: 'last7Days',
    label: 'Últimos 7 dias',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().subtract(6, 'days')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'lastWeek',
    label: 'Semana passada (Dom - Sáb)',
    getRange: () => {
      const lastSunday = moment().subtract(1, 'week').startOf('week');
      const lastSaturday = lastSunday.clone().endOf('week');
      return { start: startOfDay(lastSunday), end: endOfDay(lastSaturday) };
    }
  },
  {
    key: 'last28Days',
    label: 'Últimos 28 dias',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().subtract(27, 'days')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'last30Days',
    label: 'Últimos 30 dias',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().subtract(29, 'days')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'thisMonth',
    label: 'Este mês',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().startOf('month')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'lastMonth',
    label: 'Mês passado',
    getRange: () => {
      const lastMonth = moment().subtract(1, 'month');
      return {
        start: startOfDay(lastMonth.clone().startOf('month')),
        end: endOfDay(lastMonth.clone().endOf('month'))
      };
    }
  },
  {
    key: 'last90Days',
    label: 'Últimos 90 dias',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().subtract(89, 'days')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'thisYear',
    label: 'Este ano',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().startOf('year')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'custom',
    label: 'Personalizado',
    getRange: () => ({ start: null, end: null })
  }
];

export const getPresetByKey = (key: DateRangePresetKey): DateRangePreset | undefined =>
  DATE_RANGE_PRESETS.find(preset => preset.key === key);

export const detectPreset = (
  start: Date | string | null | undefined,
  end: Date | string | null | undefined
): DateRangePresetKey => {
  if (!start || !end) return 'custom';

  const startStr = toDateOnlyString(start);
  const endStr = toDateOnlyString(end);
  if (!startStr || !endStr) return 'custom';

  for (const preset of DATE_RANGE_PRESETS) {
    if (preset.key === 'custom') continue;
    const range = preset.getRange();
    if (
      range.start &&
      range.end &&
      toDateOnlyString(range.start) === startStr &&
      toDateOnlyString(range.end) === endStr
    ) {
      return preset.key;
    }
  }

  return 'custom';
};

export const formatDateBR = (date: Date | string | null | undefined): string => {
  const parsed = parseDateOnly(date);
  if (!parsed) return '';
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${parsed.getFullYear()}`;
};

export const parseDateBR = (value: string): Date | null => {
  if (!value) return null;
  const parsed = moment(value, 'DD/MM/YYYY', true);
  return parsed.isValid() ? parsed.startOf('day').toDate() : null;
};

export const formatDateRangeLabel = (
  start: Date | string | null | undefined,
  end: Date | string | null | undefined
): string => {
  if (!start && !end) return '';
  if (start && end) return `${formatDateBR(start)} - ${formatDateBR(end)}`;
  if (start) return formatDateBR(start);
  return formatDateBR(end);
};
